import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import glacierCopilotAuth from '../../utils/glacierCopilotAuth';
import {
    ChatContainer,
    ChatHeader,
    ChatMessagesContainer,
    ChatInputContainer,
    MessageBubble,
    AssistantAvatar,
    UserAvatar,
    InputField,
    SendButton,
    WelcomeScreen,
    WelcomeTitle,
    SuggestionButton,
    ChatHistoryList,
    ChatHistoryItem,
    GlobalChatStyles,
    ToggleSidebarButton
} from './GlaciesAIChat.styles';

const STORAGE_KEY = "glacies_chat_thread_id";

const GlaciesAIChat = () => {
    const { t } = useTranslation('common');
    const { user } = useContext(UserContext);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [activeChatId, setActiveChatId] = useState(null);
    const [chatThreads, setChatThreads] = useState([]);
    const [copilotUser, setCopilotUser] = useState(null);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [modalTable, setModalTable] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

    // Inject global styles
    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = GlobalChatStyles;
        document.head.appendChild(styleEl);
        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    // Authenticate with Copilot API on mount
    useEffect(() => {
        authenticateCopilot();
    }, []);

    const authenticateCopilot = async () => {
        setIsAuthenticating(true);
        try {
            const copilotUserInfo = await glacierCopilotAuth.ensureAuthenticated();
            setCopilotUser(copilotUserInfo);
            toast.success('Connected to Glacies AI');
            // Load threads after authentication
            await refreshThreads();
        } catch (error) {
            console.error('Failed to authenticate with Copilot API:', error);
            toast.error('Failed to connect to Glacies AI. Please try again later.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    const refreshThreads = async () => {
        if (!glacierCopilotAuth.isAuthenticated()) {
            console.log('Not authenticated, skipping thread refresh');
            return;
        }

        try {
            const apiBase = glacierCopilotAuth.getApiBase();
            const res = await fetch(`${apiBase}/api/chat/threads`, {
                credentials: 'include'
            });
            
            if (res.status === 401) {
                toast.error('Session expired. Reconnecting...');
                await authenticateCopilot();
                return;
            }
            if (!res.ok) {
                throw new Error(`Failed to fetch threads: ${res.status}`);
            }
            const threads = await res.json();
            setChatThreads(threads);
        } catch (error) {
            console.error('Error fetching threads:', error);
            toast.error('Failed to load chat history');
        }
    };

    const loadThread = async (chatThreadId) => {
        try {
            setActiveChatId(chatThreadId);
            setMessages([]);
            
            const apiBase = glacierCopilotAuth.getApiBase();
            const res = await fetch(`${apiBase}/api/chat/threads/${encodeURIComponent(chatThreadId)}/messages`, {
                credentials: 'include'
            });
            
            if (!res.ok) {
                throw new Error(`Failed to load messages: ${res.status}`);
            }
            
            const msgs = await res.json();
            const formattedMessages = msgs.map(m => {
                const csvFiles = m.csv_files || [];
                
                // Try to extract tables from meta_data if available
                let tables = [];
                if (m.meta_data && m.meta_data.structured_answer && m.meta_data.structured_answer.tables) {
                    tables = m.meta_data.structured_answer.tables;
                }
                
                // Merge table preview data into csvFiles
                const enrichedCsvFiles = csvFiles.map((csv, idx) => {
                    const matchingTable = tables.find(t => t.title === csv.title);
                    if (matchingTable) {
                        return {
                            ...csv,
                            preview_data: {
                                columns: matchingTable.columns,
                                rows: matchingTable.rows,
                                total_rows: matchingTable.row_count
                            }
                        };
                    }
                    return csv;
                });
                
                return {
                    id: m.id,
                    text: m.content,
                    sender: m.role === 'user' ? 'user' : 'assistant',
                    timestamp: new Date(m.created_at),
                    csvFiles: enrichedCsvFiles
                };
            });
            
            setMessages(formattedMessages);
            localStorage.setItem(STORAGE_KEY, chatThreadId);
            await refreshThreads();
        } catch (error) {
            console.error('Error loading thread:', error);
            toast.error('Failed to load conversation');
        }
    };

    const ensureChatThread = async () => {
        if (activeChatId) return activeChatId;
        
        try {
            const apiBase = glacierCopilotAuth.getApiBase();
            const res = await fetch(`${apiBase}/api/chat/threads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({})
            });
            
            if (!res.ok) {
                throw new Error(`Failed to create thread: ${res.status}`);
            }
            
            const thread = await res.json();
            setActiveChatId(thread.id);
            localStorage.setItem(STORAGE_KEY, thread.id);
            await refreshThreads();
            return thread.id;
        } catch (error) {
            console.error('Error creating thread:', error);
            throw error;
        }
    };

    const streamMessage = async (chatThreadId, question) => {
        try {
            const apiBase = glacierCopilotAuth.getApiBase();
            
            // Prepare request body with attachments if any
            const requestBody = { question };
            
            if (attachments.length > 0) {
                requestBody.attachments = attachments.map(att => ({
                    filename: att.name,
                    content_type: att.type,
                    data: att.base64,
                    size: att.size
                }));
            }
            
            const res = await fetch(`${apiBase}/api/chat/threads/${encodeURIComponent(chatThreadId)}/message/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });
            
            if (!res.ok) {
                throw new Error(`Stream failed: ${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let pendingAnswer = '';
            let sawSummarize = false;
            let displayedAnswer = false;

            // Add temporary assistant message
            const tempId = `temp-${Date.now()}`;
            setMessages(prev => [...prev, {
                id: tempId,
                text: '',
                sender: 'assistant',
                timestamp: new Date(),
                isTyping: true
            }]);

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const chunk = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);

                    const line = chunk.split('\n').find(l => l.startsWith('data:'));
                    if (!line) continue;

                    const dataStr = line.slice('data:'.length).trim();
                    let obj = null;
                    try {
                        obj = JSON.parse(dataStr);
                    } catch (_) {
                        continue;
                    }

                    if (!obj) continue;

                    // Handle error events
                    if (obj.type === 'error') {
                        setMessages(prev => prev.map(m => 
                            m.id === tempId 
                                ? { ...m, text: obj.error || 'Error occurred', isTyping: false, isError: true }
                                : m
                        ));
                        toast.error(obj.error || 'An error occurred');
                        return;
                    }

                    // Handle interrupt events
                    if (obj.type === 'interrupted') {
                        setMessages(prev => prev.map(m => 
                            m.id === tempId 
                                ? { ...m, text: 'Clarification needed. Please try again.', isTyping: false, isError: true }
                                : m
                        ));
                        return;
                    }

                    // Handle saved event
                    if (obj.type === 'saved') {
                        // Fetch the complete message with CSV files
                        if (obj.message_id) {
                            const apiBase = glacierCopilotAuth.getApiBase();
                            const msgRes = await fetch(`${apiBase}/api/chat/threads/${encodeURIComponent(chatThreadId)}/messages`, {
                                credentials: 'include'
                            });
                            if (msgRes.ok) {
                                const msgs = await msgRes.json();
                                const savedMsg = msgs.find(m => m.id === obj.message_id);
                                if (savedMsg) {
                                    // Extract tables from meta_data
                                    let tables = [];
                                    if (savedMsg.meta_data && savedMsg.meta_data.structured_answer && savedMsg.meta_data.structured_answer.tables) {
                                        tables = savedMsg.meta_data.structured_answer.tables;
                                    }
                                    
                                    // Enrich CSV files with table preview data
                                    const enrichedCsvFiles = (savedMsg.csv_files || []).map((csv, idx) => {
                                        const matchingTable = tables.find(t => t.title === csv.title);
                                        if (matchingTable) {
                                            return {
                                                ...csv,
                                                preview_data: {
                                                    columns: matchingTable.columns,
                                                    rows: matchingTable.rows,
                                                    total_rows: matchingTable.row_count
                                                }
                                            };
                                        }
                                        return csv;
                                    });
                                    
                                    setMessages(prev => prev.map(m => 
                                        m.id === tempId 
                                            ? {
                                                id: savedMsg.id,
                                                text: savedMsg.content,
                                                sender: 'assistant',
                                                timestamp: new Date(savedMsg.created_at),
                                                csvFiles: enrichedCsvFiles,
                                                isTyping: false
                                            }
                                            : m
                                    ));
                                }
                            }
                        }
                        continue;
                    }

                    // Extract answer from streaming events
                    if (obj.event && obj.data !== undefined) {
                        const payload = obj.data;
                        
                        if (obj.event === 'updates' && payload && typeof payload === 'object' && payload.summarize) {
                            sawSummarize = true;
                        }

                        // Extract answer
                        const answer = extractAnswerFromPayload(payload);
                        if (answer) {
                            pendingAnswer = answer;
                        }

                        // Update message when we have answer and saw summarize
                        if (!displayedAnswer && sawSummarize && pendingAnswer) {
                            displayedAnswer = true;
                            setMessages(prev => prev.map(m => 
                                m.id === tempId 
                                    ? { ...m, text: pendingAnswer, isTyping: false }
                                    : m
                            ));
                        }
                    }
                }
            }

            // If we never displayed but have an answer, show it now
            if (!displayedAnswer && pendingAnswer) {
                setMessages(prev => prev.map(m => 
                    m.id === tempId 
                        ? { ...m, text: pendingAnswer, isTyping: false }
                        : m
                ));
            } else if (!displayedAnswer) {
                setMessages(prev => prev.map(m => 
                    m.id === tempId 
                        ? { ...m, text: 'No response received. Please try again.', isTyping: false, isError: true }
                        : m
                ));
            }

            await refreshThreads();
        } catch (error) {
            console.error('Error streaming message:', error);
            throw error;
        }
    };

    const extractAnswerFromPayload = (payload) => {
        if (!payload || typeof payload !== 'object') return null;
        
        if (typeof payload.answer === 'string' && payload.answer.trim()) {
            return payload.answer;
        }
        if (typeof payload.final_response === 'string' && payload.final_response.trim()) {
            return payload.final_response;
        }
        
        if (payload.values && typeof payload.values === 'object') {
            const a = payload.values.answer || payload.values.final_response;
            if (typeof a === 'string' && a.trim()) return a;
        }
        
        return null;
    };

    const handleSendMessage = async () => {
        if ((!inputValue.trim() && attachments.length === 0) || isLoading) return;

        const userMessage = {
            id: `user-${Date.now()}`,
            text: inputValue || '(Attachment)',
            sender: 'user',
            timestamp: new Date(),
            attachments: [...attachments]
        };

        setMessages(prev => [...prev, userMessage]);
        const question = inputValue || '(Attachment sent)';
        setInputValue('');
        const currentAttachments = [...attachments];
        setAttachments([]); // Clear attachments after sending
        setIsLoading(true);

        try {
            const chatThreadId = await ensureChatThread();
            await streamMessage(chatThreadId, question);
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message. Please try again.');
            setMessages(prev => [...prev, {
                id: `error-${Date.now()}`,
                text: 'Failed to get response. Please try again.',
                sender: 'assistant',
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        await processFiles(files);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        setIsDragging(false);
        const files = Array.from(event.dataTransfer.files);
        await processFiles(files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const processFiles = async (files) => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'text/csv'];
        
        for (const file of files) {
            if (file.size > maxSize) {
                toast.error(`${file.name} is too large. Max size is 10MB.`);
                continue;
            }

            if (!allowedTypes.includes(file.type)) {
                toast.error(`${file.name} type not supported. Supported: images, PDF, text, CSV.`);
                continue;
            }

            try {
                const base64 = await fileToBase64(file);
                const attachment = {
                    id: `att-${Date.now()}-${Math.random()}`,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    base64: base64,
                    preview: file.type.startsWith('image/') ? base64 : null
                };
                
                setAttachments(prev => [...prev, attachment]);
                toast.success(`${file.name} attached`);
            } catch (error) {
                console.error('Error processing file:', error);
                toast.error(`Failed to process ${file.name}`);
            }
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result.split(',')[1]; // Remove data:image/png;base64, prefix
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const removeAttachment = (attachmentId) => {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    };

    const handleNewThread = () => {
        setActiveChatId(null);
        setMessages([]);
        localStorage.removeItem(STORAGE_KEY);
        refreshThreads();
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const openTableModal = (table) => {
        setModalTable(table);
    };

    const closeTableModal = () => {
        setModalTable(null);
    };

    const renderTable = (table, messageId, tableIndex) => {
            return (
                <div key={tableIndex} style={{
                    marginTop: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.1)',
                    width: '100%',
                    maxWidth: '100%'
                }}>
                    <div style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.03)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                                📊 {table.title}
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.7 }}>
                                {table.row_count} rows
                            </div>
                        </div>
                        <button
                            onClick={() => openTableModal(table)}
                            style={{
                                background: 'rgba(138, 92, 217, 0.3)',
                                border: 'none',
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(138, 92, 217, 0.5)'}
                            onMouseLeave={(e) => e.target.style.background = 'rgba(138, 92, 217, 0.3)'}
                        >
                            👁️ View Table
                        </button>
                    </div>
                </div>
            );
        }

    return (
        <ChatContainer>
            {isAuthenticating ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                    flexDirection: 'column',
                    gap: '20px'
                }}>
                    <AssistantAvatar style={{ width: '60px', height: '60px', fontSize: '28px' }}>G</AssistantAvatar>
                    <div style={{ fontSize: '18px', opacity: 0.8 }}>Connecting to Glacies AI...</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <span className="typing-dot">.</span>
                        <span className="typing-dot">.</span>
                        <span className="typing-dot">.</span>
                    </div>
                </div>
            ) : (
                <>
                    <ChatHistoryList isCollapsed={isSidebarCollapsed}>
                        <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, opacity: 0.9 }}>Chats</h3>
                            <button 
                                onClick={handleNewThread}
                                style={{
                                    background: 'linear-gradient(135deg, #8a5cd9, #6a4cb8)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '6px 14px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px rgba(138, 92, 217, 0.3)',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(138, 92, 217, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 8px rgba(138, 92, 217, 0.3)';
                                }}
                            >
                                + New
                            </button>
                        </div>
                        {chatThreads.length === 0 ? (
                            <div style={{ padding: '20px', opacity: 0.5, fontSize: '13px', textAlign: 'center' }}>
                                No chats yet. Start a conversation!
                            </div>
                        ) : (
                            chatThreads.map((thread) => (
                                <ChatHistoryItem 
                                    key={thread.id}
                                    onClick={() => loadThread(thread.id)}
                                    className={activeChatId === thread.id ? 'active' : ''}
                                >
                                    <div style={{ fontWeight: 500, marginBottom: '6px', fontSize: '14px' }}>
                                        {thread.title || 'New conversation'}
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.5 }}>
                                        {thread.updated_at ? new Date(thread.updated_at).toLocaleDateString() : ''}
                                    </div>
                                </ChatHistoryItem>
                            ))
                        )}
                    </ChatHistoryList>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <ChatHeader>
                            <ToggleSidebarButton 
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                title={isSidebarCollapsed ? "Show chat history" : "Hide chat history"}
                            >
                                {isSidebarCollapsed ? '☰' : '✕'}
                            </ToggleSidebarButton>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <AssistantAvatar>G</AssistantAvatar>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Glacies Assistant</h2>
                                    <p style={{ margin: 0, fontSize: '14px', opacity: 0.7 }}>
                                        Hi, {user?.firstName || 'there'}! How can I help you?
                                    </p>
                                </div>
                            </div>
                        </ChatHeader>

                <ChatMessagesContainer
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={isDragging ? 'dragging' : ''}
                >
                    {isDragging && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(154, 60, 139, 0.2)',
                            border: '2px dashed #9A3C8B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            fontWeight: 600,
                            zIndex: 10,
                            pointerEvents: 'none'
                        }}>
                            Drop files here to attach
                        </div>
                    )}
                    {messages.length === 0 ? (
                        <WelcomeScreen>
                            <WelcomeTitle>What would you like to review?</WelcomeTitle>
                            {/* <SuggestionButton onClick={() => setInputValue('Tell me about company knowledge')}>
                                ✨ Company knowledge
                            </SuggestionButton> */}
                        </WelcomeScreen>
                    ) : (
                        <>
                            {messages.map((message) => (
                                <MessageBubble key={message.id} sender={message.sender}>
                                    {message.sender === 'assistant' && <AssistantAvatar>G</AssistantAvatar>}
                                    <div style={{ flex: 1 }}>
                                        {message.sender === 'assistant' && (
                                            <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                                                Glacies Assistant
                                            </div>
                                        )}
                                        {message.sender === 'user' && (
                                            <div style={{ 
                                                fontWeight: 600, 
                                                marginBottom: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                {user?.profile_picture ? (
                                                    <img 
                                                        src={user.profile_picture} 
                                                        alt={`${user.firstName} ${user.lastName}`}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                ) : (
                                                    <UserAvatar />
                                                )}
                                                {user?.firstName && user?.lastName 
                                                    ? `${user.firstName} ${user.lastName}` 
                                                    : user?.firstName || 'You'}
                                            </div>
                                        )}
                                        {message.isTyping ? (
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                                <span className="typing-dot">.</span>
                                            </div>
                                        ) : (
                                            <div style={{ color: message.isError ? '#ff6b6b' : 'inherit' }}>
                                                {message.sender === 'assistant' ? (
                                                    <ReactMarkdown 
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            p: ({node, ...props}) => <p style={{ margin: '0 0 12px 0', lineHeight: '1.6' }} {...props} />,
                                                            ul: ({node, ...props}) => <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                                                            ol: ({node, ...props}) => <ol style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                                                            li: ({node, ...props}) => <li style={{ margin: '4px 0' }} {...props} />,
                                                            strong: ({node, ...props}) => <strong style={{ fontWeight: 700, color: '#fff' }} {...props} />,
                                                            code: ({node, inline, ...props}) => 
                                                                inline ? (
                                                                    <code style={{
                                                                        background: 'rgba(255,255,255,0.1)',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.9em',
                                                                        fontFamily: 'monospace'
                                                                    }} {...props} />
                                                                ) : (
                                                                    <code style={{
                                                                        display: 'block',
                                                                        background: 'rgba(255,255,255,0.1)',
                                                                        padding: '12px',
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.9em',
                                                                        fontFamily: 'monospace',
                                                                        overflowX: 'auto',
                                                                        margin: '8px 0'
                                                                    }} {...props} />
                                                                ),
                                                            a: ({node, ...props}) => <a style={{ color: '#9A3C8B', textDecoration: 'underline' }} {...props} />,
                                                            h1: ({node, ...props}) => <h1 style={{ fontSize: '1.5em', fontWeight: 700, margin: '16px 0 8px 0' }} {...props} />,
                                                            h2: ({node, ...props}) => <h2 style={{ fontSize: '1.3em', fontWeight: 700, margin: '14px 0 8px 0' }} {...props} />,
                                                            h3: ({node, ...props}) => <h3 style={{ fontSize: '1.1em', fontWeight: 700, margin: '12px 0 8px 0' }} {...props} />,
                                                        }}
                                                    >
                                                        {message.text}
                                                    </ReactMarkdown>
                                                ) : (
                                                    message.text
                                                )}
                                            </div>
                                        )}
                                        {message.attachments && message.attachments.length > 0 && (
                                            <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {message.attachments.map((att) => (
                                                    <div key={att.id} style={{
                                                        background: 'rgba(255,255,255,0.1)',
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        maxWidth: '200px'
                                                    }}>
                                                        {att.preview ? (
                                                            <img 
                                                                src={`data:${att.type};base64,${att.base64}`}
                                                                alt={att.name}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                background: 'rgba(255,255,255,0.2)',
                                                                borderRadius: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '20px'
                                                            }}>
                                                                📄
                                                            </div>
                                                        )}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ 
                                                                fontSize: '12px', 
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {att.name}
                                                            </div>
                                                            <div style={{ fontSize: '10px', opacity: 0.7 }}>
                                                                {(att.size / 1024).toFixed(1)} KB
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {message.csvFiles && message.csvFiles.length > 0 && (
                                            <div style={{ marginTop: '12px' }}>
                                                {message.csvFiles.map((csv, idx) => (
                                                    <div key={idx}>
                                                        {csv.preview_data && csv.preview_data.columns && csv.preview_data.rows ? (
                                                            renderTable({
                                                                title: csv.title,
                                                                columns: csv.preview_data.columns,
                                                                rows: csv.preview_data.rows,
                                                                row_count: csv.row_count
                                                            }, message.id, idx)
                                                        ) : (
                                                            <div style={{
                                                                background: 'rgba(255,255,255,0.1)',
                                                                padding: '12px',
                                                                borderRadius: '8px',
                                                                marginBottom: '8px'
                                                            }}>
                                                                <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                                                    📊 {csv.title}
                                                                </div>
                                                                <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '8px' }}>
                                                                    {csv.row_count} rows
                                                                </div>
                                                            </div>
                                                        )}
                                                        <a 
                                                            href={csv.url} 
                                                            download={`${csv.title}.csv`}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                background: 'linear-gradient(135deg, #8a5cd9, #6a4cb8)',
                                                                color: 'white',
                                                                textDecoration: 'none',
                                                                padding: '10px 20px',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                marginTop: '8px',
                                                                marginBottom: '12px',
                                                                transition: 'all 0.2s',
                                                                boxShadow: '0 2px 8px rgba(138, 92, 217, 0.3)'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.transform = 'translateY(-2px)';
                                                                e.target.style.boxShadow = '0 4px 12px rgba(138, 92, 217, 0.4)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.transform = 'translateY(0)';
                                                                e.target.style.boxShadow = '0 2px 8px rgba(138, 92, 217, 0.3)';
                                                            }}
                                                        >
                                                            ⬇️ Download CSV
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </MessageBubble>
                            ))}
                        </>
                    )}
                </ChatMessagesContainer>

                <ChatInputContainer>
                    {attachments.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginBottom: '12px',
                            padding: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '12px'
                        }}>
                            {attachments.map((att) => (
                                <div key={att.id} style={{
                                    background: 'rgba(154, 60, 139, 0.3)',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    maxWidth: '200px'
                                }}>
                                    {att.preview ? (
                                        <img 
                                            src={`data:${att.type};base64,${att.base64}`}
                                            alt={att.name}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                objectFit: 'cover',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            background: 'rgba(255,255,255,0.2)',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '16px'
                                        }}>
                                            📄
                                        </div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            fontSize: '12px', 
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {att.name}
                                        </div>
                                        <div style={{ fontSize: '10px', opacity: 0.7 }}>
                                            {(att.size / 1024).toFixed(1)} KB
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeAttachment(att.id)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'white',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            fontSize: '16px',
                                            opacity: 0.7,
                                            transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                                        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', position: 'relative' }}>
                        <input
                            type="file"
                            id="file-input"
                            multiple
                            accept="image/*,.pdf,.txt,.csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button
                            onClick={() => document.getElementById('file-input').click()}
                            disabled={isLoading}
                            style={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '12px',
                                padding: '12px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                color: 'white',
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isLoading ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => !isLoading && (e.target.style.background = 'rgba(255,255,255,0.1)')}
                            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.06)'}
                            title="Attach files (images, PDF, text, CSV)"
                        >
                            📎
                        </button>
                        <InputField
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask Glacies AI or attach files..."
                            disabled={isLoading}
                            style={{ flex: 1 }}
                        />
                        <SendButton 
                            onClick={handleSendMessage} 
                            disabled={(!inputValue.trim() && attachments.length === 0) || isLoading}
                            style={{ position: 'static', marginBottom: '0' }}
                        >
                            ➤
                        </SendButton>
                    </div>
                    <div style={{ 
                        fontSize: '12px', 
                        textAlign: 'center', 
                        opacity: 0.6,
                        marginTop: '8px'
                    }}>
                        Glacies AI is for assisting you with current services. It is not a clinical tool.
                    </div>
                </ChatInputContainer>
            </div>
            </>
            )}

            {/* Table Modal */}
            {modalTable && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}
                    onClick={closeTableModal}
                >
                    <div 
                        style={{
                            background: '#1a1a2e',
                            borderRadius: '16px',
                            maxWidth: '95vw',
                            maxHeight: '90vh',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'rgba(138, 92, 217, 0.1)'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                                    📊 {modalTable.title}
                                </h3>
                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.7 }}>
                                    {modalTable.row_count} rows total
                                </p>
                            </div>
                            <button
                                onClick={closeTableModal}
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    color: 'white',
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Body - Scrollable Table */}
                        <div 
                            className="table-scroll-container"
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                padding: '0'
                            }}
                        >
                            <table style={{
                                width: '100%',
                                minWidth: 'max-content',
                                borderCollapse: 'collapse',
                                fontSize: '14px'
                            }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr style={{ background: '#0f0f1e' }}>
                                        {modalTable.columns.map((col, idx) => (
                                            <th key={idx} style={{
                                                padding: '16px 20px',
                                                textAlign: col.align || 'left',
                                                fontWeight: 600,
                                                borderBottom: '2px solid rgba(138, 92, 217, 0.5)',
                                                whiteSpace: 'nowrap',
                                                background: '#0f0f1e'
                                            }}>
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalTable.rows.map((row, rowIdx) => (
                                        <tr key={rowIdx} style={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(138, 92, 217, 0.08)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            {modalTable.columns.map((col, colIdx) => (
                                                <td key={colIdx} style={{
                                                    padding: '14px 20px',
                                                    textAlign: col.align || 'left',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {row[col.key] !== null && row[col.key] !== undefined 
                                                        ? String(row[col.key]) 
                                                        : '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </ChatContainer>
    );
};

export default GlaciesAIChat;
